import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { getFromStorage, setOnStorage } from '@libs/utils/helpers/handleStorage';
import { OfferBenefitFilter, OfferCategoryFilter } from '../types/offers';

type FilterStorage = {
  category?: string;
  benefits?: string;
};

const CATEGORY_MAP = {
  cartao: 'cards',
  cartoes: 'cards',
  cartao_de_credito: 'cards',
  cartoes_de_credito: 'cards',
  'cartao-de-credito': 'cards',
  'cartoes-de-credito': 'cards',
  emprestimo: 'loans',
  emprestimos: 'loans',
  conta: 'account',
  contas: 'account',
  todos: 'all',
};

const BENEFIT_MAP = {
  cards: {
    pre_aprovado: 'pre_approved',
    pre_aprovados: 'pre_approved',
    'pre-aprovados': 'pre_approved',
    'pre-aprovado': 'pre_approved',
    zero_anuidade: 'zero_annuity',
    anuidade_zero: 'zero_annuity',
    sem_anuidade: 'zero_annuity',
    'zero-anuidade': 'zero_annuity',
    'anuidade-zero': 'zero_annuity',
    'sem-anuidade': 'zero_annuity',
    milha: 'miles',
    milhas: 'miles',
    pontos: 'miles',
    ponto: 'miles',
    'milhas-e-pontos': 'miles',
    'milha-e-ponto': 'miles',
    cashback: 'cashback',
    todos: 'all',
  },
  loans: {
    pessoal: 'personal',
    garantia: 'assurance',
    consignado: 'consigned',
    pj: 'pj',
    receba_rapido: 'quick_received',
    'receba-rapido': 'quick_received',
    todos: 'all',
  },
};

const FILTER_STORAGE_KEY = 'ck_of';

export function useOfferFilters() {
  const location = useLocation();
  const navigate = useNavigate();

  const [category, setCategory] = useState<string>();
  const [benefit, setBenefit] = useState<string>();

  useEffect(() => {
    const fromUrl = urlSource(location?.search);
    const fromStorage = storageSource();

    const nextCategory = fromUrl?.category || fromStorage?.category || 'all';
    const nextBenefit = fromUrl?.benefits || fromStorage?.benefits || 'all';

    setCategory(nextCategory);
    setBenefit(nextBenefit);

    persistUrlFilters(fromUrl);
  }, [location?.search]);

  function urlSource(searchParams?: string) {
    const params = new URLSearchParams(searchParams);
    const categoryParam = params.get('categoria');
    const benefitParam = params.get('beneficio');

    const categorySlug = getCategorySlug(categoryParam);
    const benefitSlug = getBenefitSlug(categoryParam, benefitParam);

    const urlSourceResult = {
      category: categorySlug,
      benefits: benefitSlug,
    };

    return urlSourceResult;
  }

  function storageSource() {
    const storage = getFromStorage<FilterStorage>(FILTER_STORAGE_KEY);

    const storageSourceResult = {
      category: storage?.category,
      benefits: safeCategoryBenefit(storage?.category, storage?.benefits),
    };

    return storageSourceResult;
  }

  function persistUrlFilters(fromUrl?: FilterStorage) {
    if (!fromUrl?.category && !fromUrl?.benefits) {
      return;
    }

    if (fromUrl?.category) {
      persistFilter(fromUrl?.category, 'category');
    }

    if (fromUrl?.benefits) {
      persistFilter(fromUrl?.benefits, 'benefits');
    }

    cleanFilterParamsFromUrl();
  }

  function cleanFilterParamsFromUrl() {
    const params = new URLSearchParams(location?.search);
    if (params.has('categoria')) params.delete('categoria');
    if (params.has('beneficio')) params.delete('beneficio');
    const newSearch = params.toString();
    const newUrl = `${location?.pathname}${newSearch ? `?${newSearch}` : ''}`;
    if (typeof window !== 'undefined' && window.history?.replaceState) {
      window.history.replaceState(null, '', newUrl);
    }
  }

  function getCategorySlug(urlCategoryParam?: string | null) {
    if (!urlCategoryParam) return;
    return CATEGORY_MAP?.[urlCategoryParam as keyof typeof CATEGORY_MAP];
  }

  function getBenefitSlug(urlCategoryParam?: string | null, urlBenefitParam?: string | null) {
    if (!urlCategoryParam || !urlBenefitParam) return;

    const categorySlug = getCategorySlug(urlCategoryParam);
    if (!categorySlug) return;

    const availableCategories = Object.keys(BENEFIT_MAP);
    if (!availableCategories.includes(categorySlug)) return;

    const benefitMap = BENEFIT_MAP?.[categorySlug as keyof typeof BENEFIT_MAP];
    const benefitSlug = benefitMap?.[urlBenefitParam as keyof typeof benefitMap];

    return benefitSlug;
  }

  function safeCategoryBenefit(categoryValue?: string, benefitValue?: string) {
    if (!categoryValue) return;

    const availableCategories = Object.keys(BENEFIT_MAP);
    if (!availableCategories.includes(categoryValue)) return;

    const benefitMap = BENEFIT_MAP?.[categoryValue as keyof typeof BENEFIT_MAP];
    const benefitSlug = Object.values(benefitMap).find((benefit) => benefit === benefitValue);
    return benefitSlug;
  }

  function persistFilter(value: string, filterType: string) {
    const persistedFilter = getFromStorage<FilterStorage>(FILTER_STORAGE_KEY) || {};
    const newFilter = {
      ...persistedFilter,
      [filterType]: value,
    };
    setOnStorage('session', FILTER_STORAGE_KEY, newFilter);

    if (filterType === 'benefits') {
      setBenefit(value);
    } else {
      setBenefit('all');
    }
  }

  function goToOffersPage({ path, category, benefit }: { path: string; category?: OfferCategoryFilter; benefit?: OfferBenefitFilter }) {
    setOnStorage('session', FILTER_STORAGE_KEY, {
      category: category,
      benefits: benefit,
    });

    navigate(path);
  }

  return {
    filterCategory: category,
    filterBenefit: benefit,
    persistFilter,
    goToOffersPage,
    categoryFilters: CATEGORY_MAP,
    benefitFilters: BENEFIT_MAP,
  };
}
